import { z } from 'zod';
// ============= Pipeline Types =============
/**
 * CI/CD platform types.
 */
export const CIPlatform = z.enum([
    'github-actions',
    'gitlab-ci',
    'jenkins',
    'azure-pipelines',
    'bitbucket-pipelines',
    'circleci',
    'travis-ci',
    'unknown',
]);
/**
 * Pipeline stage types.
 */
export const PipelineStageType = z.enum([
    'checkout',
    'setup',
    'build',
    'test',
    'lint',
    'security',
    'deploy',
    'release',
    'notify',
    'cleanup',
    'custom',
]);
/**
 * Environment types.
 */
export const EnvironmentType = z.enum([
    'development',
    'staging',
    'production',
    'test',
    'preview',
    'custom',
]);
/**
 * Pipeline step definition.
 */
export const PipelineStepSchema = z.object({
    id: z.string(),
    name: z.string(),
    command: z.string().optional(),
    uses: z.string().optional(), // For GitHub Actions
    with: z.record(z.string()).optional(),
    env: z.record(z.string()).optional(),
});
/**
 * Pipeline job definition.
 */
export const PipelineJobSchema = z.object({
    id: z.string(),
    name: z.string(),
    stageType: PipelineStageType,
    steps: z.array(PipelineStepSchema),
    needs: z.array(z.string()).optional(), // Job dependencies
    runsOn: z.string().optional(),
    environment: EnvironmentType.optional(),
    condition: z.string().optional(),
    matrix: z.record(z.array(z.string())).optional(),
});
/**
 * Pipeline stage (group of jobs).
 */
export const PipelineStageSchema = z.object({
    id: z.string(),
    name: z.string(),
    type: PipelineStageType,
    jobs: z.array(z.string()), // Job IDs in this stage
    order: z.number(),
});
/**
 * Pipeline workflow/pipeline definition.
 */
export const PipelineWorkflowSchema = z.object({
    id: z.string(),
    name: z.string(),
    triggers: z.array(z.string()).optional(), // push, pull_request, etc.
    branches: z.array(z.string()).optional(),
    jobs: z.array(PipelineJobSchema),
});
/**
 * Complete pipeline diagram data.
 */
export const PipelineDiagramDataSchema = z.object({
    metadata: z.object({
        title: z.string(),
        description: z.string().optional(),
        platform: CIPlatform,
        sourceFiles: z.array(z.string()).optional(),
        generatedAt: z.date().optional(),
    }),
    stages: z.array(PipelineStageSchema),
    jobs: z.array(PipelineJobSchema),
    workflows: z.array(PipelineWorkflowSchema).optional(),
});
// ============= Helper Functions =============
/**
 * Infer stage type from job/step name.
 */
export function inferStageType(name) {
    const lower = name.toLowerCase();
    if (lower.includes('checkout') || lower.includes('clone'))
        return 'checkout';
    if (lower.includes('setup') || lower.includes('install') || lower.includes('dependencies'))
        return 'setup';
    if (lower.includes('build') || lower.includes('compile'))
        return 'build';
    if (lower.includes('test') || lower.includes('spec') || lower.includes('jest') || lower.includes('pytest'))
        return 'test';
    if (lower.includes('lint') || lower.includes('eslint') || lower.includes('prettier') || lower.includes('format'))
        return 'lint';
    if (lower.includes('security') || lower.includes('scan') || lower.includes('audit') || lower.includes('snyk'))
        return 'security';
    if (lower.includes('deploy') || lower.includes('publish') || lower.includes('push'))
        return 'deploy';
    if (lower.includes('release') || lower.includes('tag') || lower.includes('version'))
        return 'release';
    if (lower.includes('notify') || lower.includes('slack') || lower.includes('email'))
        return 'notify';
    if (lower.includes('cleanup') || lower.includes('clean'))
        return 'cleanup';
    return 'custom';
}
/**
 * Infer environment from job/deploy target.
 */
export function inferEnvironment(name) {
    const lower = name.toLowerCase();
    if (lower.includes('prod') || lower.includes('production') || lower.includes('release'))
        return 'production';
    if (lower.includes('stag') || lower.includes('staging') || lower.includes('stage'))
        return 'staging';
    if (lower.includes('dev') || lower.includes('development'))
        return 'development';
    if (lower.includes('test') || lower.includes('qa'))
        return 'test';
    if (lower.includes('preview') || lower.includes('pr-'))
        return 'preview';
    return 'custom';
}
/**
 * Get stage color.
 */
export function getStageColor(stageType) {
    switch (stageType) {
        case 'checkout':
            return '#e0e7ff';
        case 'setup':
            return '#dbeafe';
        case 'build':
            return '#dcfce7';
        case 'test':
            return '#fef3c7';
        case 'lint':
            return '#fce7f3';
        case 'security':
            return '#fee2e2';
        case 'deploy':
            return '#d1fae5';
        case 'release':
            return '#f3e8ff';
        case 'notify':
            return '#e0f2fe';
        case 'cleanup':
            return '#f1f5f9';
        default:
            return '#f1f5f9';
    }
}
/**
 * Convert PipelineDiagramData to UnifiedDiagramData.
 */
export function pipelineToUnifiedDiagram(data) {
    const nodes = [];
    const edges = [];
    // Create nodes for each job
    let xOffset = 0;
    const stageXPositions = new Map();
    // Position stages horizontally
    for (const stage of data.stages.sort((a, b) => a.order - b.order)) {
        stageXPositions.set(stage.id, xOffset);
        xOffset += 250;
    }
    // Create job nodes
    for (const job of data.jobs) {
        const stage = data.stages.find(s => s.jobs.includes(job.id));
        const stageX = stage ? stageXPositions.get(stage.id) || 0 : 0;
        const stageJobIndex = stage ? stage.jobs.indexOf(job.id) : 0;
        nodes.push({
            id: job.id,
            type: 'service',
            label: job.name,
            position: {
                x: stageX,
                y: stageJobIndex * 120,
            },
            data: {
                stageType: job.stageType,
                environment: job.environment,
                runsOn: job.runsOn,
                stepCount: job.steps.length,
            },
        });
        // Create edges for job dependencies
        if (job.needs) {
            for (const dep of job.needs) {
                edges.push({
                    id: `${dep}-${job.id}`,
                    source: dep,
                    target: job.id,
                    type: 'default',
                    animated: false,
                });
            }
        }
    }
    return {
        metadata: {
            title: data.metadata.title,
            description: data.metadata.description,
            diagramType: 'pipelineDiagram',
            category: 'operations',
            layoutDirection: 'LR',
            layoutAlgorithm: 'dagre',
            sourceFiles: data.metadata.sourceFiles,
            generatedAt: data.metadata.generatedAt,
            generatedBy: 'regex',
        },
        nodes,
        edges,
    };
}
//# sourceMappingURL=pipeline.js.map